/**
 * useGameTimer — Client-side guess timer for the GameContext (PR-V3.E split).
 *
 * Responsibilities (only these — SRP):
 *   1. While `status === PLAYING` and `start > 0`, schedule a single
 *      `setTimeout` that fires `fetchTick(token)` once GUESS_DURATION elapses.
 *   2. If GUESS_DURATION has already elapsed (server tick was missed due to
 *      network latency), call `handleTimeUp()` synchronously inside the
 *      effect so the client recovers immediately.
 *   3. Guard the tick:
 *        - `isTickingRef`         -> no concurrent tick calls.
 *        - `lastTickedCurrentRef` -> no double-tick for the same `current`.
 *   4. Clean up the pending timeout on unmount or on dependency change.
 *
 * Timing buffer: the scheduled timeout fires at GUESS_DURATION + 100ms (a
 * small client-side scheduling buffer). The server-side TIMER_TOLERANCE
 * (1000ms) lives in `src/lib/supabase/tick/index.ts` — DO NOT touch.
 *
 * Behaviour pinned by `src/__tests__/context/gameContext.test.tsx` (timer +
 * tick block, 6 tests).
 */

import { useCallback, useEffect, useRef } from "react"

import { PLAYING, GameStatus } from "../../utils/gameStates"
import { GUESS_DURATION } from "../../utils/gameConfig"
import { fetchTick } from "../../utils/appTick"

interface UseGameTimerArgs {
  token: string | null
  status: GameStatus | null
  start: number
  current: number
  refetchGame: () => Promise<void>
}

export const useGameTimer = ({
  token,
  status,
  start,
  current,
  refetchGame,
}: UseGameTimerArgs): void => {
  const timerTriggerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isTickingRef = useRef<boolean>(false)
  const lastTickedCurrentRef = useRef<number>(-1)

  const handleTimeUp = useCallback(async () => {
    // Prevent concurrent tick calls
    if (isTickingRef.current) {
      return
    }

    // Prevent double-ticking the same question
    if (lastTickedCurrentRef.current === current) {
      return
    }

    if (token && status === PLAYING) {
      isTickingRef.current = true
      lastTickedCurrentRef.current = current
      try {
        await fetchTick(token)
        await refetchGame()
      } catch (err) {
        console.error("[GameContext] Error triggering tick:", err)
      } finally {
        isTickingRef.current = false
      }
    }
  }, [token, status, current, refetchGame])

  useEffect(() => {
    if (timerTriggerRef.current) {
      clearTimeout(timerTriggerRef.current)
      timerTriggerRef.current = null
    }

    if (status !== PLAYING || !start) {
      return
    }

    const endTimeMs = start * 1000 + GUESS_DURATION
    const now = Date.now()
    const timeUntilExpiry = endTimeMs - now

    if (timeUntilExpiry > 0) {
      timerTriggerRef.current = setTimeout(() => {
        handleTimeUp()
      }, timeUntilExpiry + 100)
    } else {
      // Timer already expired (due to network latency), trigger tick immediately
      handleTimeUp()
    }

    return () => {
      if (timerTriggerRef.current) {
        clearTimeout(timerTriggerRef.current)
        timerTriggerRef.current = null
      }
    }
  }, [start, status, current, handleTimeUp])
}
